import {
  ArrowUpRight,
  CalendarDays,
  CircleCheck,
  Filter,
  MapPin,
  RefreshCcw,
  Ticket as TicketIcon,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { adminApi, type DashboardFilters } from "../../../api/client";
import LoadingState from "../../../components/common/LoadingState";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import type { DashboardSummary } from "../../../types/api";
import { formatCompactDate, formatCurrency, formatDateTime } from "../../../utils/format";

const statusBadge: Record<string, string> = {
  paid: "ego-badge ego-badge-success",
  successful: "ego-badge ego-badge-success",
  pending: "ego-badge ego-badge-warning",
  failed: "ego-badge ego-badge-danger",
  refunded: "ego-badge ego-badge-neutral",
};

const chartColors = {
  gold: "#f5b303",
  charcoal: "#0b1220",
  blue: "#2563eb",
  green: "#16a34a",
  red: "#dc2626",
  slate: "#64748b",
  amber: "#d97706",
};

const orderStatusColors: Record<string, string> = {
  paid: chartColors.green,
  pending: chartColors.amber,
  failed: chartColors.red,
  refunded: chartColors.slate,
};

function badgeFor(status?: string) {
  if (!status) return "ego-badge ego-badge-neutral";
  return statusBadge[status.toLowerCase()] ?? "ego-badge ego-badge-neutral";
}

function defaultDashboardFilters(): DashboardFilters {
  const year = new Date().getFullYear();
  return {
    purchaseStart: `${year}-01-01`,
    purchaseEnd: `${year}-12-31`,
    eventStart: `${year}-01-01`,
    eventEnd: `${year}-12-31`,
  };
}

function compactNumber(value: number) {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

function shortDate(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(value));
}

function tooltipFormatter(value: unknown, name: unknown) {
  const label = String(name);
  const numeric = Number(value);
  if (label.toLowerCase().includes("revenue")) {
    return [formatCurrency(Number.isFinite(numeric) ? numeric : 0), label];
  }
  return [Number.isFinite(numeric) ? compactNumber(numeric) : String(value), label];
}

export default function AdminDashboardPage() {
  const { admin, token } = useAdminAuth();
  const [draftFilters, setDraftFilters] = useState<DashboardFilters>(() => defaultDashboardFilters());
  const [activeFilters, setActiveFilters] = useState<DashboardFilters>(() => defaultDashboardFilters());
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    if (!token) return;
    try {
      const response = await adminApi.dashboard(token, activeFilters);
      setSummary(response);
      setError(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Could not load dashboard");
    }
  }, [activeFilters, token]);

  useEffect(() => {
    if (!token) return;

    let active = true;
    const run = async () => {
      try {
        const response = await adminApi.dashboard(token, activeFilters);
        if (active) {
          setSummary(response);
          setError(null);
        }
      } catch (requestError) {
        if (active) {
          setError(requestError instanceof Error ? requestError.message : "Could not load dashboard");
        }
      }
    };

    void run();
    const intervalId = window.setInterval(() => void run(), 15000);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [activeFilters, token]);

  const salesByDay = summary?.salesByDay ?? [];
  const eventPerformance = summary?.eventPerformance ?? [];
  const topEventChartData = eventPerformance.slice(0, 8);
  const checkInChartData = eventPerformance
    .filter((event) => event.ticketsSold > 0 || event.checkedIn > 0)
    .slice(0, 8)
    .map((event) => ({
      ...event,
      awaitingCheckIn: Math.max(event.ticketsSold - event.checkedIn, 0),
    }));
  const orderBreakdown = summary?.orderStatusBreakdown ?? [];
  const orderBreakdownTotal = orderBreakdown.reduce((sum, item) => sum + item.count, 0);
  const canViewOrders = Boolean(admin?.effectivePermissions?.includes("orders:view"));

  const stats = [
    {
      label: "Gross revenue",
      value: formatCurrency(summary?.stats.grossRevenue ?? 0),
      icon: Wallet,
      tint: "bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]",
    },
    {
      label: "Tickets sold",
      value: String(summary?.stats.ticketsSold ?? 0),
      icon: TicketIcon,
      tint: "bg-[#eff6ff] dark:bg-[rgba(59,130,246,0.14)] text-[#1d4ed8]",
    },
    {
      label: "Checked in",
      value: String(summary?.stats.checkedIn ?? 0),
      icon: CircleCheck,
      tint: "bg-[#ecfdf5] dark:bg-[rgba(16,185,129,0.14)] text-[#047857]",
    },
    {
      label: "Live screenings",
      value: String(summary?.stats.publishedEvents ?? 0),
      icon: CalendarDays,
      tint: "bg-[#f5f3ff] dark:bg-[rgba(139,92,246,0.14)] text-[#6d28d9]",
    },
    {
      label: "Cities",
      value: String(summary?.stats.activeCities ?? 0),
      icon: MapPin,
      tint: "bg-[#fff7ed] dark:bg-[rgba(245,158,11,0.10)] text-[#9a3412]",
    },
  ];

  const headlineEvent = eventPerformance[0];
  const hasLoaded = Boolean(summary);

  if (!summary && !error) {
    return <LoadingState label="Loading dashboard..." />;
  }

  if (error && !summary) {
    return (
      <div className="ego-surface px-6 py-12 text-center text-sm font-semibold text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <form
        className="ego-surface p-5"
        onSubmit={(submitEvent) => {
          submitEvent.preventDefault();
          setActiveFilters(draftFilters);
        }}
      >
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold text-foreground">
              <Filter size={16} /> Analytics filters
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Purchase dates filter orders; screening dates filter scheduled screenings.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6 xl:items-end">
            <DateField
              label="Purchase start"
              value={draftFilters.purchaseStart ?? ""}
              onChange={(value) => setDraftFilters((current) => ({ ...current, purchaseStart: value }))}
            />
            <DateField
              label="Purchase end"
              value={draftFilters.purchaseEnd ?? ""}
              onChange={(value) => setDraftFilters((current) => ({ ...current, purchaseEnd: value }))}
            />
            <DateField
              label="Screening start"
              value={draftFilters.eventStart ?? ""}
              onChange={(value) => setDraftFilters((current) => ({ ...current, eventStart: value }))}
            />
            <DateField
              label="Screening end"
              value={draftFilters.eventEnd ?? ""}
              onChange={(value) => setDraftFilters((current) => ({ ...current, eventEnd: value }))}
            />
            <button type="submit" className="ego-btn-primary xl:mb-0.5">
              Apply
            </button>
            <button
              type="button"
              className="ego-btn-secondary xl:mb-0.5"
              onClick={() => {
                const defaults = defaultDashboardFilters();
                setDraftFilters(defaults);
                setActiveFilters(defaults);
              }}
            >
              <RefreshCcw size={15} /> Clear
            </button>
          </div>
        </div>
      </form>

      {error && (
        <div className="ego-surface border-l-4 border-l-red-500 px-5 py-4 text-sm font-semibold text-red-600">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="ego-surface p-5">
              <div className="flex items-start justify-between">
                <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${stat.tint}`}>
                  <Icon size={18} />
                </div>
                <span className="ego-badge ego-badge-neutral">Live</span>
              </div>
              <div className="mt-5 text-2xl font-bold tracking-tight text-foreground">{stat.value}</div>
              <div className="mt-1 text-sm text-muted-foreground">{stat.label}</div>
            </div>
          );
        })}
      </div>

      {headlineEvent && (
        <div className="ego-surface overflow-hidden">
          <div className="grid gap-6 p-6 lg:grid-cols-[1.4fr_1fr] lg:p-8">
            <div>
              <span className="ego-badge ego-badge-brand">
                <TrendingUp size={12} /> Top performing
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-foreground">
                {headlineEvent.title}
              </h2>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={14} /> {headlineEvent.city || "No city"}
                </span>
                <span>{formatCompactDate(headlineEvent.date)}</span>
                <span className={badgeFor(headlineEvent.status)}>{headlineEvent.status}</span>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                <MetricBox label="Sold" value={headlineEvent.ticketsSold} />
                <MetricBox label="Revenue" value={formatCurrency(headlineEvent.revenue)} />
                <MetricBox label="Sell-through" value={`${headlineEvent.sellThrough}%`} />
              </div>
            </div>

            <div className="ego-surface-muted p-6">
              <div className="ego-eyebrow">Reporting window</div>
              <h3 className="mt-3 text-xl font-bold text-foreground">
                {activeFilters.purchaseStart && activeFilters.purchaseEnd
                  ? `${formatCompactDate(activeFilters.purchaseStart)} - ${formatCompactDate(activeFilters.purchaseEnd)}`
                  : "All purchase dates"}
              </h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Screening filters{" "}
                {activeFilters.eventStart || activeFilters.eventEnd
                  ? `${activeFilters.eventStart || "start"} to ${activeFilters.eventEnd || "end"}`
                  : "are not applied"}
                .
              </p>
              <button type="button" onClick={() => void loadSummary()} className="ego-btn-secondary mt-5">
                <RefreshCcw size={15} /> Refresh data
              </button>
            </div>
          </div>
        </div>
      )}

      {!headlineEvent && hasLoaded && (
        <div className="ego-surface px-6 py-12 text-center">
          <h2 className="text-lg font-bold text-foreground">No analytics for this filter set</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Adjust the purchase or screening date filters to include screenings with paid orders.
          </p>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[1.35fr_0.65fr]">
        <ChartCard
          title="Revenue and ticket sales"
          subtitle="Paid orders grouped by purchase date"
          isEmpty={salesByDay.length === 0}
        >
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={salesByDay} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.18)" />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="revenue"
                tickFormatter={(value) => compactNumber(Number(value))}
                tick={{ fontSize: 12 }}
                width={46}
              />
              <YAxis
                yAxisId="tickets"
                orientation="right"
                tickFormatter={(value) => compactNumber(Number(value))}
                tick={{ fontSize: 12 }}
                width={36}
              />
              <Tooltip formatter={tooltipFormatter} labelFormatter={(label) => formatCompactDate(String(label))} />
              <Legend />
              <Bar yAxisId="revenue" dataKey="revenue" name="Revenue" fill={chartColors.gold} radius={[6, 6, 0, 0]} />
              <Line
                yAxisId="tickets"
                type="monotone"
                dataKey="ticketsSold"
                name="Tickets sold"
                stroke={chartColors.blue}
                strokeWidth={2.4}
                dot={{ r: 3 }}
              />
              <Line
                yAxisId="tickets"
                type="monotone"
                dataKey="checkedIn"
                name="Checked in"
                stroke={chartColors.green}
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Order status"
          subtitle="Payment outcomes in this window"
          isEmpty={orderBreakdownTotal === 0}
        >
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={orderBreakdown}
                dataKey="count"
                nameKey="status"
                innerRadius={72}
                outerRadius={108}
                paddingAngle={3}
              >
                {orderBreakdown.map((entry) => (
                  <Cell key={entry.status} fill={orderStatusColors[entry.status] ?? chartColors.slate} />
                ))}
              </Pie>
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <ChartCard
          title="Top screenings by revenue"
          subtitle="Paid revenue with ticket counts in the selected filters"
          isEmpty={topEventChartData.length === 0}
        >
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={topEventChartData} layout="vertical" margin={{ top: 10, right: 18, left: 26, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.18)" />
              <XAxis type="number" tickFormatter={(value) => compactNumber(Number(value))} tick={{ fontSize: 12 }} />
              <YAxis
                dataKey="title"
                type="category"
                tick={{ fontSize: 12 }}
                width={120}
                tickFormatter={(value) => String(value).slice(0, 18)}
              />
              <Tooltip formatter={tooltipFormatter} />
              <Bar dataKey="revenue" name="Revenue" fill={chartColors.charcoal} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Check-in progress"
          subtitle="Checked-in tickets vs sold tickets awaiting validation"
          isEmpty={checkInChartData.length === 0}
        >
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={checkInChartData} margin={{ top: 10, right: 18, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.18)" />
              <XAxis
                dataKey="title"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => String(value).slice(0, 10)}
              />
              <YAxis tickFormatter={(value) => compactNumber(Number(value))} tick={{ fontSize: 12 }} width={36} />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              <Bar dataKey="checkedIn" name="Checked in" stackId="tickets" fill={chartColors.green} radius={[5, 5, 0, 0]} />
              <Bar dataKey="awaitingCheckIn" name="Awaiting check-in" stackId="tickets" fill={chartColors.gold} radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.3fr_1fr]">
        <div className="ego-surface overflow-hidden">
          <div className="flex items-center justify-between border-b border-line px-6 py-5">
            <div>
              <h2 className="text-lg font-bold text-foreground">Screening performance</h2>
              <p className="text-sm text-muted-foreground">Revenue, sell-through, and attendance leaders</p>
            </div>
          </div>

          <div className="ego-divide">
            {eventPerformance.slice(0, 8).map((event, index) => (
              <Link
                key={event.id}
                to={`/admin/screenings/${event.id}`}
                className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 transition hover:bg-surface-muted"
              >
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-sm font-bold text-[#c2410c]">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-foreground">{event.title}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <MapPin size={12} /> {event.city || "No city"}
                      <span className={badgeFor(event.status)}>{event.status}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-foreground">{formatCurrency(event.revenue)}</div>
                  <div className="text-xs text-muted-foreground">
                    {event.ticketsSold} sold · {event.sellThrough}% sell-through
                  </div>
                </div>
              </Link>
            ))}
            {eventPerformance.length === 0 && (
              <div className="px-6 py-10 text-center text-sm text-muted-foreground">
                No screening sales yet.
              </div>
            )}
          </div>
        </div>

        {canViewOrders && (
          <div className="ego-surface overflow-hidden">
            <div className="flex items-center justify-between border-b border-line px-6 py-5">
              <div>
                <h2 className="text-lg font-bold text-foreground">Recent orders</h2>
                <p className="text-sm text-muted-foreground">Latest buyer activity in the selected window</p>
              </div>
              <Link to="/admin/orders" className="ego-btn-ghost text-sm text-[var(--pp-gold)]">
                View all <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="ego-divide">
              {(summary?.recentOrders ?? []).slice(0, 6).map((order) => {
                const checkedIn = (order.tickets ?? []).filter((ticket) => ticket.status === "checked-in").length;
                const total = (order.tickets ?? []).length;
                return (
                  <div key={order.reference} className="px-6 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-foreground">
                          {order.customer?.name || "Legacy customer"}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-muted-foreground">
                          {order.event?.title || "Legacy order"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-foreground">{formatCurrency(order.total)}</div>
                        <div className="mt-0.5 text-[11px] uppercase tracking-wide text-[#9aa3b2]">
                          {order.reference}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className={badgeFor(order.paymentStatus)}>{order.paymentStatus}</span>
                      <span className="ego-badge ego-badge-neutral">
                        {checkedIn}/{total} checked in
                      </span>
                      <span className="ml-auto text-[11px] text-[#9aa3b2]">
                        {formatDateTime(order.createdAt)}
                      </span>
                    </div>
                  </div>
                );
              })}
              {(!summary || summary.recentOrders.length === 0) && (
                <div className="px-6 py-10 text-center text-sm text-muted-foreground">No orders yet.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="ego-input !py-2"
      />
    </label>
  );
}

function MetricBox({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="ego-surface-muted p-4">
      <div className="ego-eyebrow">{label}</div>
      <div className="mt-2 text-xl font-bold text-foreground">{value}</div>
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  isEmpty,
  children,
}: {
  title: string;
  subtitle: string;
  isEmpty: boolean;
  children: ReactNode;
}) {
  return (
    <div className="ego-surface overflow-hidden">
      <div className="border-b border-line px-6 py-5">
        <h2 className="text-lg font-bold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="p-5">
        {isEmpty ? (
          <div className="flex h-[300px] items-center justify-center rounded-xl border border-dashed border-line bg-surface-muted px-6 text-center text-sm font-semibold text-muted-foreground">
            No analytics data for this filter set.
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
